import { useCallback, useMemo } from "react";
import useAuthContext from "./useAuthContext";
import { SupabaseInstance } from "@/integrations/supabase";
import { AuthServices } from "@/apis";
import { Logger } from "@/utils";

export default function useAuth() {
  const {
    session,
    user,
    loading,
    initialAuthChecked,
    recoveryPassword,
    handleSetUser,
    handleSetLoading,
    handleInitUpdatePasswordFlow,
  } = useAuthContext();

  const isRecoverySession = useMemo(() => {
    if (!session?.user) return false;
    return session?.user?.user_metadata?.is_recovery_session;
  }, [session]);

  const onSignOut = useCallback(async () => {
    try {
      const { error } = await SupabaseInstance.logout();
      if (error) throw error;
    } catch (error) {
      Logger.loggerError({
        section: "sign_out",
        message: error,
      });
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      if (!session?.refresh_token) {
        return null;
      }
      const { data, error } = await SupabaseInstance.refreshSession(
        session.refresh_token,
      );
      if (error) throw error;
      return data;
    } catch (error) {
      Logger.loggerError({
        section: "refresh_session",
        message: error,
      });
      return null;
    }
  }, [session?.refresh_token]);

  // Fetches fresh user data from the server and updates the auth context.
  // Unlike refreshSession (which only refreshes the token and fires TOKEN_REFRESHED),
  // this ensures user_metadata is current — needed after webhook-driven updates.
  const refreshUser = useCallback(async () => {
    try {
      const { data: { user: freshUser }, error } = await SupabaseInstance
        .getUser();
      if (error) throw error;
      if (freshUser) handleSetUser(freshUser);
      return freshUser;
    } catch (error) {
      Logger.loggerError({
        section: "refresh_user",
        message: error,
      });
      return null;
    }
  }, [handleSetUser]);

  const onUpdatePasswordFlow = useCallback(
    async () => {
      try {
        if (!session?.access_token || !session?.refresh_token) {
          throw new Error("Missing tokens");
        }

        handleInitUpdatePasswordFlow();
        const response = await AuthServices.setUpdatePasswordFlow({
          access_token: session?.access_token,
          refresh_token: session?.refresh_token,
        });
        return {
          error: response.error,
          session: response.session,
          sessionError: response.sessionError,
          updatePassword: response.updatePassword,
        };
      } catch (error) {
        Logger.loggerError({
          section: "on_update_password_flow",
          message: error,
        });
        throw error;
      } finally {
        handleSetLoading(false);
      }
    },
    [session, handleSetLoading, handleInitUpdatePasswordFlow],
  );

  return {
    session,
    user,
    loading,
    initialAuthChecked,
    isRecoverySession,
    recoveryPassword,
    onSignOut,
    onUpdatePasswordFlow,
    refreshSession,
    refreshUser,
  };
}
